// Collapsible section wrapper shared by every proptree-settings page.
// Header bar has an icon (left), a title + optional subtitle (centre),
// and a chevron toggle (right). Click anywhere on the header to expand
// or collapse the body. The body animates smoothly via the CSS
// grid-template-rows trick — no JS height measurement needed.
//
// Each consumer opts in to opening on first mount via `defaultOpen`.
// The default is closed so long pages land on a compact overview.
import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card } from './ui/card.js';
import { cn } from '../lib/utils.js';

export function SectionCard({
  title,
  subtitle,
  icon: Icon,
  defaultOpen = false,
  // Set false when the header should be informational only — no
  // chevron, no click target, body always visible. Used by pages where
  // the user doesn't need to compact-collapse sections (e.g. Billing,
  // where each section is short and there's no scrolling to manage).
  collapsible = true,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  defaultOpen?: boolean;
  collapsible?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const effectiveOpen = collapsible ? open : true;
  const headerInner = (
    <>
      {Icon && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[15px] font-medium leading-tight text-foreground">
          {title}
        </span>
        {subtitle && (
          <span className="mt-1 block font-sans text-[12px] leading-tight text-muted-foreground">
            {subtitle}
          </span>
        )}
      </span>
      {collapsible && (
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 ease-out',
            !open && '-rotate-90',
          )}
        />
      )}
    </>
  );
  return (
    <Card className="overflow-hidden p-0">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={cn(
            'flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-muted/40',
            open && 'border-b border-[hsl(var(--hairline))]',
          )}
        >
          {headerInner}
        </button>
      ) : (
        <div className="flex w-full items-center gap-3 border-b border-[hsl(var(--hairline))] px-5 py-3">
          {headerInner}
        </div>
      )}
      {/* Smooth collapse via the grid-rows trick: the outer container
          animates between `0fr` (collapsed) and `1fr` (expanded). The
          inner `overflow-hidden` clips content during the transition.
          When `collapsible` is false, `effectiveOpen` stays true so the
          body simply stays visible without any animation glitches. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          effectiveOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </Card>
  );
}
